
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { mockClients } from "@/data/mockData";
import type { Client } from "@/types";

type TimeFrame = 'week' | 'month' | 'year';
type Profile = {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  specialization: string | null;
  tier: string | null;
  avatar_url: string | null;
};

export function useDashboardData() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [timeFrame, setTimeFrame] = useState<TimeFrame>('week');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [coachClients, setCoachClients] = useState<typeof mockClients>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user && isMounted) {
          navigate('/auth');
          return;
        }

        if (!user) return;

        // Fetch profile data
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error("Error fetching profile:", profileError);
          return;
        }

        if (profileData && isMounted) {
          setProfile(profileData);
        }

        // Fetch clients data
        const { data: clientsData, error: clientsError } = await supabase
          .from('clients')
          .select('*')
          .eq('coach_id', user.id);

        if (clientsError) {
          console.error("Error fetching clients:", clientsError);
          if (isMounted) {
            toast({
              title: "Error loading clients",
              description: "There was a problem loading your client data. Please try again.",
              variant: "destructive",
            });
          }
          return;
        }

        if (clientsData && isMounted) {
          console.log('Fetched clients data:', clientsData.length, 'clients');
          const transformedClients = clientsData.map(client => ({
            id: client.client_id,
            name: client.full_name || '',
            avatar: '/placeholder.svg',
            joinDate: client.created_at,
            status: 'active' as const,
            currentPlan: {
              name: 'Basic Training',
              progress: {
                current: 0,
                total: 100
              }
            },
            progress: {
              completionRate: [0],
              performance: [0]
            },
            notes: [],
            programs: [] // Add empty programs array to satisfy the type requirement
          }));
          setCoachClients(transformedClients);
        }
      } catch (error) {
        console.error("Error in fetchUserData:", error);
        if (isMounted) {
          toast({
            title: "Error",
            description: "There was a problem loading your dashboard. Please try again.",
            variant: "destructive",
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUserData();
    
    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isMounted = false;
    };
  }, [navigate, toast]);

  const handleTimeFrameChange = (value: string) => {
    setTimeFrame(value as TimeFrame);
  };

  return {
    isLoading,
    profile,
    coachClients,
    timeFrame,
    handleTimeFrameChange
  };
}
